import { useEffect, useState } from 'react'
import type { VehicleSummary } from '../../api/client'
import { Mark } from '../../components/Btn'
import { Panel, Section, SectionHead, Wrap } from '../../components/layout'
import { isAllClearDismissed, setAllClearDismissed } from '../../lib/dismissed'
import { AppLink } from '../../lib/link'
import { countdownText, type RenewalUrgency } from '../../lib/renewal'
import type { ScreenId } from '../../shell/nav'

interface Alert {
  key: string
  /** The uppercase mono kicker. Carries the state in text, before any colour does. */
  kicker: string
  headline: string
  body: string
  to: ScreenId
  cta: string
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/**
 * Needs attention — the one panel that decides what the page is *for* on any given day.
 *
 * The design's version is a single hardcoded story: the two K-series head-gasket checks, 19 days overdue, with
 * "Mark both done" wired to a `setState` that moves a counter and fires a toast describing a write that never
 * happens. The story is a good one and it is BT53's, not the product's — a Freelander's oil-filler-cap check is
 * a `CheckDefinition` row, and the panel cannot know it is special.
 *
 * So this derives its alerts, in severity order, and each one is a real fact with somewhere to go. An expired
 * renewal outranks an overdue check: one is a car that is illegal to drive, the other is a look under the
 * bonnet. When there is nothing wrong it says so — the design's `is-ok` state, which it only reaches by
 * clicking its own fake button.
 */
export function AttentionPanel({ summary }: { summary: VehicleSummary }) {
  const { checks, renewals, mileage } = summary
  const reg = summary.registration
  const alerts: Alert[] = []

  const named = [
    { r: renewals.mot, to: 'service' as const },
    { r: renewals.insurance, to: 'vehicle-info' as const },
    { r: renewals.roadTax, to: 'vehicle-info' as const },
  ]

  // 1. Expired. The negative days-remaining the domain deliberately keeps, put to work.
  for (const { r, to } of named) {
    if (r.daysRemaining !== null && r.daysRemaining < 0) {
      alerts.push({
        key: `expired-${r.name}`,
        kicker: `${r.name} · expired`,
        headline: `${r.name} expired ${countdownText(r.daysRemaining)}`,
        body: `Ran out on ${new Date(`${r.expiryDate}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. This is not a reminder — it is a car that should not be on the road until it is sorted.`,
        to,
        cta: `Open ${r.name.toLowerCase()}`,
      })
    }
  }

  // 2. Due inside 30 days. Urgent, and emphatically not the same as expired.
  for (const { r, to } of named) {
    if (r.daysRemaining !== null && r.daysRemaining >= 0 && (r.urgency as RenewalUrgency | null) === 'Red') {
      alerts.push({
        key: `due-${r.name}`,
        kicker: `${r.name} · due`,
        headline: `${r.name} expires in ${countdownText(r.daysRemaining)}`,
        body: `Under the 30-day threshold. ${r.source ?? 'No source recorded'}.`,
        to,
        cta: `Open ${r.name.toLowerCase()}`,
      })
    }
  }

  // 3. The mileage flag. A reading above the latest one means a record disagrees with the odometer — spec §5.3
  // says flag it, never silently accept it. This is the 83,000 mi row, surfaced.
  if (mileage.hasNonMonotonicHistory) {
    alerts.push({
      key: 'mileage',
      kicker: 'Mileage · not monotonic',
      headline: 'A logged reading is above the current odometer',
      body:
        mileage.highestRecordedMileage !== null && mileage.currentMileage !== null
          ? `The highest reading on record is ${mileage.highestRecordedMileage.toLocaleString('en-GB')} mi, against a latest of ${mileage.currentMileage.toLocaleString('en-GB')} mi. A mileage cannot go down, so one of the two is a typo. Flagged, not corrected, and not silently accepted.`
          : 'A reading is above the latest one. A mileage cannot go down, so one of them is a typo.',
      to: 'mileage',
      cta: 'Open mileage log',
    })
  }

  // 4. A named early-warning watch that has lapsed — the thing this panel could never say before.
  //
  // The design leads with "Head-gasket watch · lapsed", and the original port could not: nothing modelled WHICH
  // checks were the head-gasket watch, so the panel could only count. Now an issue names its own checks, and
  // this ranks ABOVE the generic overdue alert below because the two are different claims. "7 checks are
  // overdue" is a chore list; "the two checks keeping the head-gasket issue resolved have stopped happening" is
  // the reason the chore list matters. Still below an expired renewal — that is a car that cannot legally move.
  for (const watch of summary.watches ?? []) {
    if (watch.lapsedCheckCount === 0) continue

    const all = watch.lapsedCheckCount === watch.totalCheckCount
    alerts.push({
      key: `watch-${watch.issueId}`,
      kicker: `${watch.issueTitle} · watch lapsed`,
      headline: all
        ? `${plural(watch.totalCheckCount, 'The check', `All ${watch.totalCheckCount} checks`)} watching this issue ${plural(watch.totalCheckCount, 'has', 'have')} lapsed`
        : `${watch.lapsedCheckCount} of ${watch.totalCheckCount} checks watching this issue ${plural(watch.lapsedCheckCount, 'has', 'have')} lapsed`,
      body:
        watch.issueStatus === 'Resolved'
          ? `This issue is marked Resolved, and these checks are what keep it that way — resolved is contingent on them, not permanent. Its status is deliberately unchanged: a lapsed watch is flagged, never acted on for you.`
          : `These checks are the early warning for an issue still being monitored, and they have stopped reporting. Overdue, never logged, or flagged on the last log all count — a check that is not being done is not reassurance.`,
      to: 'checks',
      cta: 'Open regular checks',
    })
  }

  // 5. Overdue checks, as one alert rather than seven.
  if (checks.overdueCount > 0) {
    alerts.push({
      key: 'checks',
      kicker: 'Regular checks · lapsed',
      headline: `${checks.overdueCount} ${plural(checks.overdueCount, 'check is', 'checks are')} past ${plural(checks.overdueCount, 'its', 'their')} interval`,
      body: `Each is past the cadence set for it. Status is computed from the last log and the interval, so logging one moves it out of this count immediately.`,
      to: 'checks',
      cta: 'Open regular checks',
    })
  }

  // 6. Flagged checks — a bad verdict (Attention/Failed) on the last log, which needs looking at whatever the
  // date says. This is the signal the head-gasket watch depends on, so it raises its own alert.
  if (checks.attentionCount > 0) {
    alerts.push({
      key: 'flagged-checks',
      kicker: 'Regular checks · flagged',
      headline: `${checks.attentionCount} ${plural(checks.attentionCount, 'check was', 'checks were')} flagged on ${plural(checks.attentionCount, 'its', 'their')} last log`,
      body: `Logged Attention or Failed — the verdict needs acting on regardless of when the check is next due. Re-logging one as OK clears it.`,
      to: 'checks',
      cta: 'Open regular checks',
    })
  }

  // The all-clear can be dismissed and remembered (per vehicle). Auto-reset: the moment something genuinely
  // needs attention the flag is cleared, so once it is resolved the fresh all-clear shows again — and can be
  // dismissed anew. The alerting branch is never dismissible. Seeded from storage so there is no flash.
  const [dismissed, setDismissed] = useState(() => alerts.length === 0 && isAllClearDismissed(reg))
  useEffect(() => {
    if (alerts.length > 0) {
      setAllClearDismissed(reg, false)
      setDismissed(false)
    } else {
      setDismissed(isAllClearDismissed(reg))
    }
  }, [reg, alerts.length])

  // Dismissed and nothing to say: the section disappears entirely rather than leave an empty header.
  if (dismissed && alerts.length === 0) return null

  const rule =
    alerts.length === 0
      ? 'nothing outstanding'
      : `${alerts.length} ${plural(alerts.length, 'alert', 'alerts')} · ${checks.overdueCount + checks.dueSoonCount + checks.attentionCount} checks outstanding`

  return (
    <Section>
      <Wrap>
        <SectionHead
          title="Needs attention"
          rule={<>{rule}</>}
          link={
            <AppLink className="sec-link" to="checks" reg={reg}>
              Regular checks →
            </AppLink>
          }
        />

        {alerts.length === 0 ? (
          <Panel className="attn is-ok">
            <div>
              <div className="attn-k">Nothing overdue</div>
              <h3>Nothing is overdue, expired, or flagged</h3>
              <p>
                No renewal is inside 30 days, no check is past its interval, and no reading contradicts the
                odometer. Computed at render — if that changes, this panel says so without anyone updating it.
                {/* Never-logged is not overdue, so it raises no alert — but it is not clear either, and the
                    domain is emphatic that it is "not an error, not a default, and emphatically not Ok". The
                    kicker says "Nothing overdue" rather than "All clear" for the same reason: this panel
                    should claim exactly what it checked. Reading a never-logged check as fine is how the
                    workbook came to count 17 definitions out of 18. */}
                {checks.neverLoggedCount > 0 && (
                  <>
                    {' '}
                    {checks.neverLoggedCount}{' '}
                    {plural(checks.neverLoggedCount, 'check has', 'checks have')} never been logged, so{' '}
                    {plural(checks.neverLoggedCount, 'it has', 'they have')} no interval to be past — counted,
                    not assumed done.
                  </>
                )}
              </p>
            </div>
            <div className="attn-act">
              {checks.neverLoggedCount > 0 && (
                <AppLink className="btn ghost" to="checks" reg={reg}>
                  Open regular checks
                </AppLink>
              )}
              <Mark
                onClick={() => {
                  setAllClearDismissed(reg, true)
                  setDismissed(true)
                }}
              >
                Dismiss
              </Mark>
            </div>
          </Panel>
        ) : (
          alerts.map((a) => (
            <Panel key={a.key} className="attn">
              <div>
                <div className="attn-k">{a.kicker}</div>
                <h3>{a.headline}</h3>
                <p>{a.body}</p>
              </div>
              <div className="attn-act">
                <AppLink className="btn" to={a.to} reg={reg}>
                  {a.cta}
                </AppLink>
              </div>
            </Panel>
          ))
        )}
      </Wrap>
    </Section>
  )
}
